import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BiArrowBack, BiSave, BiSend } from "react-icons/bi";
import { toast } from "react-toastify";

import {
  getJrDetail,
  updateJrQty,
  kirimJrApproval,
  clearDetail,
  jrV2Selector,
} from "../job-request/jrV2Slice";
import { getMinRoom, roomSelector } from "../../master/room/roomSlice";
import { getMinOum, OumSelector } from "../../master/oum/oumSlice";
import { Colors } from "../../../assets/Colors";
import GeneralServiceEntryEditor from "./GeneralServiceEntryEditor";
import PortRentalEntryEditor from "./PortRentalEntryEditor";
import QtyInput, { formatQty } from "./QtyInput";

// Aktivitas yang memakai editor entri terstruktur (roll-up ActualQty sendiri)
const EDITOR_ACTS = new Set(["GENERAL_SERVICE", "PORT_SERVICES", "RENTAL", "AREA_RENTAL", "MANPOWER", "WASTE_MANAGEMENT_SERVICES"]);
// Item pendukung (ActivityName kosong) yang juga pakai editor entri.
const ENTRY_LINE_GROUPS = new Set(["EQUIPMENT", "LABOUR", "TRUCK", "TWS"]);
// Satu line dianggap dikelola editor entri (bukan input qty tunggal) bila:
const isEntryEditorLine = (l) => EDITOR_ACTS.has(l.ActivityName) || ENTRY_LINE_GROUPS.has(l.LineGroup);

const STATUS_CONFIG = {
  "-1":  { label: "Draft",             bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"   },
  "99":  { label: "Menunggu Manifest", bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"   },
  "100": { label: "Menunggu Activity", bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500" },
  "110": { label: "Revisi JR",         bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500"   },
  "150": { label: "Menunggu Approval", bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  "199": { label: "Pending Client",    bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  "200": { label: "Approved",          bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  "201": { label: "Partial Reject",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
};

const ACTIVITY_LABELS = {
  PORT_SERVICES: "Port Services", RENTAL: "Rental",
  WASTE_MANAGEMENT_SERVICES: "Waste Management Services",
  AREA_RENTAL: "Area Rental", MANPOWER: "Manpower", GENERAL_SERVICE: "General Service",
  OTHER: "Item Pendukung",
};

// Label sub-tipe General Service agar input bisa dibedakan per jenis layanan
const SUBTYPE_LABELS = {
  MEALS: "🍽️ Meals", LAUNDRY: "🧺 Laundry", ROOM: "🛏️ Accommodation / Room",
  DRILL: "💧 Fresh Water / Drill", WOODEN: "🪵 Wooden Dunnage",
  VEHICLE: "🚚 Vehicle", OTHERS: "📦 Others",
  // Bukan sub-tipe QoLine sungguhan — kunci pengelompokan tampilan saja utk
  // baris Area Rental (Fixed) yang digabung ke section General Service.
  AREA_RENTAL: "🏢 Area Rental",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[String(status)] ?? STATUS_CONFIG["-1"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

export default function ActivityDetailV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const jrID = location.state?.JrID ?? null;

  const jrV2 = useSelector(jrV2Selector);
  const data = jrV2.detail;
  const room = useSelector(roomSelector);
  const oum = useSelector(OumSelector);

  const [actionLoading, setActionLoading] = useState(false);
  const [qtyMap, setQtyMap] = useState({}); // { JrLineID: actualQty }

  useEffect(() => {
    if (jrID) dispatch(getJrDetail(jrID));
    dispatch(getMinRoom({ search: "" }));
    dispatch(getMinOum({ search: "" }));
    return () => { dispatch(clearDetail()); };
  }, [jrID]); // eslint-disable-line

  // Inisialisasi qty dari data saat detail dimuat
  useEffect(() => {
    if (data?.Lines) {
      const init = {};
      data.Lines.forEach(l => { init[l.JrLineID] = l.ActualQty ?? 0; });
      setQtyMap(init);
    }
  }, [data?.JrID]); // eslint-disable-line

  if (!jrID) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <p className="mb-2">JR tidak ditemukan</p>
          <button onClick={() => navigate("/operasional-activity")}
            className="text-sm text-orange-500 underline">Kembali</button>
        </div>
      </div>
    );
  }

  if (jrV2.isDetailLoading || !data) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">Memuat data…</div>;
  }

  const reload = () => dispatch(getJrDetail(jrID));
  // 110 = Revisi JR (hasil Recall JR Approved) — diedit seperti Menunggu Activity.
  const isActivity = data.Status === 100 || data.Status === 110;
  const lastRecall = (data.Revisions ?? [])[0];

  // Line yang dikelola editor entri sendiri (punya roll-up ActualQty) dikecualikan
  // dari simpan qty tunggal milik induk.
  const editorLineIds = new Set(
    (data.Lines ?? [])
      .filter(isEntryEditorLine)
      .map(l => l.JrLineID)
  );

  const buildQtyItems = () =>
    Object.entries(qtyMap)
      .filter(([JrLineID]) => !editorLineIds.has(parseInt(JrLineID)))
      .map(([JrLineID, ActualQty]) => ({
        JrLineID: parseInt(JrLineID),
        ActualQty: parseFloat(ActualQty) || 0,
      }));

  // Apakah ada line yang masih pakai input qty tunggal (bukan editor)?
  const hasManualQtyLines = (data.Lines ?? []).some(l => !isEntryEditorLine(l));

  const handleSaveQty = async () => {
    const items = buildQtyItems();
    if (items.length === 0) {
      toast.info("Tidak ada kuantitas untuk disimpan di sini. Entri terstruktur (General Service / Port / Rental) disimpan dari tabelnya masing-masing.");
      return;
    }
    setActionLoading(true);
    try {
      await dispatch(updateJrQty({ JrID: jrID, Items: items })).unwrap();
      toast.success("Kuantitas tersimpan");
      reload();
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal simpan qty"); }
    finally { setActionLoading(false); }
  };

  // Submit hasil activity ke rantai approval internal (Setup › Approval, grup
  // "JobRequest"). JR TIDAK lagi langsung ke client dari sini: setelah approver
  // menyetujui, status berpindah sendiri ke Pending Client.
  const handleSubmitApproval = async () => {
    const ok = window.confirm(
      `Submit Job Request ${jrID} untuk approval?\n\nKuantitas yang belum tersimpan akan ikut disimpan. ` +
      `Setelah disubmit, JR tidak dapat diedit lagi sampai approver menyetujui atau menolaknya.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      // Simpan qty terbaru dulu (line non-GS) supaya approver menilai angka final.
      const items = buildQtyItems();
      if (items.length) await dispatch(updateJrQty({ JrID: jrID, Items: items })).unwrap();
      await dispatch(kirimJrApproval(jrID)).unwrap();
      toast.success("JR disubmit untuk approval");
      navigate("/operasional-activity");
    } catch (e) { toast.error(e?.message ?? "Gagal submit untuk approval"); }
    finally { setActionLoading(false); }
  };

  // Opsi Room dari master room dengan jenis "Room"
  const roomOptions = (room.minData ?? [])
    .filter(o => o.jenis === "Room")
    .map(o => ({ value: o.value, label: o.label }));

  // Opsi UoM untuk kolom Dimensi. Dimensi memakai satuan yang berbeda dari Qty
  // (mis. Qty dalam Ton, Dimensi dalam M³), jadi harus bisa dipilih sendiri —
  // beda dengan kolom "UoM" milik Qty yang hanya menampilkan satuan kontrak.
  //
  // Sumbernya MOum (master satuan operasional — Ton/M³/M²/Barrel), BUKAN MSatuan
  // yang dipakai gudang/procurement: JrLine."SatuanID" pun merujuk MOum.
  // Nilai yang disimpan adalah namanya (mis. "M³"), sejajar dengan kolom "Uom"
  // milik Qty yang juga menyimpan nama satuan, bukan ID.
  const uomOptions = Array.from(
    new Map(
      (oum.minData ?? [])
        .map(o => [o.label ?? o.value, { value: o.label ?? o.value, label: o.label ?? o.value }])
    ).values()
  );

  // Group lines by activity. Area Rental yang Fixed (Layanan Tetap) digabung
  // tampil di bawah section General Service atas permintaan user — TAPI
  // ActivityName mentahnya (l.ActivityName) TETAP "AREA_RENTAL" di sini,
  // cuma kunci pengelompokan tampilan yang dipindah. Ini sengaja TIDAK
  // menyentuh data/rumus penagihan (resolveFormulaType di backend tetap
  // membaca ActivityName asli dari JrLine, jadi rumus berbasis UoM Area
  // Rental — mis. per-bulan utk satuan "Month" — tetap benar).
  const isAreaRentalFixed = (l) => l.ActivityName === "AREA_RENTAL" && l.ServiceType === "F";
  const grouped = (data.Lines ?? []).reduce((acc, l) => {
    const k = isAreaRentalFixed(l) ? "GENERAL_SERVICE" : (l.ActivityName || "OTHER");
    (acc[k] = acc[k] || []).push(l);
    return acc;
  }, {});

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">

      {/* Top bar */}
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate("/operasional-activity")}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge status={data.Status} />
          {isActivity && (
            <>
              {hasManualQtyLines && (
                <button onClick={handleSaveQty} disabled={actionLoading}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium"
                  style={{ borderColor: Colors.primary, color: Colors.primary }}>
                  <BiSave className="text-base" /> Simpan Kuantitas
                </button>
              )}
              <button onClick={handleSubmitApproval} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: Colors.primary }}>
                <BiSend /> Submit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Header info */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <InfoField label="No. JR"        value={data.JrID} />
          <InfoField label="No. QO"        value={data.QoNo} />
          <InfoField label="Nama Klien"    value={data.NamaKlien} />
          <InfoField label="No. Kontrak"   value={data.NoKontrak} />
          <InfoField label="Tgl Pekerjaan" value={data.WorkDate} />
          <InfoField label="SPK No"        value={data.SPKNo} />
          <InfoField label="WSB No"        value={data.WSBNo} />
          <InfoField label="Prioritas"     value={data.Priority} />
          {data.Revision > 0 && <InfoField label="Versi" value={`Rev ${data.Revision}`} />}
        </div>
      </div>

      {data.Status === 110 && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>Revisi JR — versi {data.Revision}.</strong> JR ini ditarik kembali (recall) dari status Approved.
          {lastRecall && <> Alasan: <em>{lastRecall.Reason}</em> ({lastRecall.RecalledBy}, {lastRecall.RecalledAt}).</>}
          {" "}Perbaiki activity lalu klik Submit untuk approval ulang.{" "}
          <button onClick={() => navigate("/jr-version-compare", { state: { JrID: data.JrID } })}
            className="underline font-semibold">Bandingkan dengan versi sebelumnya</button>
        </div>
      )}

      {isActivity && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-indigo-50 border border-indigo-200 text-sm text-indigo-700">
          Masukkan jumlah aktual lapangan per layanan, klik <strong>Simpan Kuantitas</strong>. Setelah selesai, klik <strong>Submit</strong> — JR masuk antrean approval sebelum diteruskan ke client.
        </div>
      )}

      {/* Lines per activity */}
      {Object.entries(grouped).map(([act, lines]) => (
        <div key={act} className="mb-5">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
              {ACTIVITY_LABELS[act] ?? act}
            </span>
            <span className="text-xs text-gray-500">{lines.length} item</span>
          </div>
          {/* General Service saat Activity → dikelompokkan per sub-tipe agar mudah dibedakan.
              Baris Area Rental (Fixed) yang digabung ke sini TETAP dirender via
              PortRentalEntryEditor (bukan GeneralServiceEntryEditor) — editor itu yang
              mendukung rumus/entri berbasis UoM (Month/Day/M²) milik Area Rental asli. */}
          {act === "GENERAL_SERVICE" && isActivity ? (
            <div className="flex flex-col gap-5">
              {Object.entries(
                lines.reduce((acc, l) => {
                  const k = isAreaRentalFixed(l) ? "AREA_RENTAL" : (l.SubType || "OTHERS");
                  (acc[k] = acc[k] || []).push(l);
                  return acc;
                }, {})
              ).map(([sub, subLines]) => (
                <div key={sub}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-700">
                      {SUBTYPE_LABELS[sub] ?? sub}
                    </span>
                    <span className="text-xs text-gray-400">{subLines.length} item</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {subLines.map(l => (
                      isAreaRentalFixed(l) ? (
                        <PortRentalEntryEditor key={l.JrLineID} line={l} jrID={jrID} uomOptions={uomOptions}
                          period={data.Period || (data.WorkDate || "").slice(0, 7)} workDate={data.WorkDate} onSaved={reload} />
                      ) : (
                        <GeneralServiceEntryEditor key={l.JrLineID} line={l} jrID={jrID} roomOptions={roomOptions}
                          period={data.Period || (data.WorkDate || "").slice(0, 7)} workDate={data.WorkDate} onSaved={reload} />
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (act === "PORT_SERVICES" || act === "RENTAL" || act === "AREA_RENTAL" || act === "MANPOWER" || act === "WASTE_MANAGEMENT_SERVICES") && isActivity ? (
            /* Port Services, Rental, Area Rental, Manpower & Waste → editor entri terstruktur per item */
            <div className="flex flex-col gap-3">
              {lines.map(l => (
                <PortRentalEntryEditor key={l.JrLineID} line={l} jrID={jrID} uomOptions={uomOptions}
                          period={data.Period || (data.WorkDate || "").slice(0, 7)} workDate={data.WorkDate} onSaved={reload} />
              ))}
            </div>
          ) : (
            <>
            {/* Item pendukung (Equipment/Labour/Truck) → editor entri saat Activity */}
            {isActivity && lines.some(l => ENTRY_LINE_GROUPS.has(l.LineGroup)) && (
              <div className="flex flex-col gap-3 mb-3">
                {lines.filter(l => ENTRY_LINE_GROUPS.has(l.LineGroup)).map(l => (
                  <PortRentalEntryEditor key={l.JrLineID} line={l} jrID={jrID} uomOptions={uomOptions}
                          period={data.Period || (data.WorkDate || "").slice(0, 7)} workDate={data.WorkDate} onSaved={reload} />
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {lines.filter(l => !(isActivity && ENTRY_LINE_GROUPS.has(l.LineGroup))).map(l => {
                const isFixed = l.ServiceType === "F";
                const wasRejected = !!l.RejectReason;
                const cardTone = wasRejected ? "bg-red-50/40 border-red-300"
                  : isFixed ? "bg-blue-50/40 border-blue-100" : "bg-white border-gray-200";
                return (
                  <div key={l.JrLineID} className={`rounded-xl border p-4 ${cardTone}`}>
                    {wasRejected && (
                      <div className="mb-2 px-3 py-2 rounded-lg bg-red-100 border border-red-200 text-xs text-red-700">
                        <span className="font-semibold">Sebelumnya ditolak klien — perlu revisi.</span> Alasan: {l.RejectReason}
                      </div>
                    )}
                    <div className={`font-semibold text-sm leading-snug mb-2 ${wasRejected ? "text-red-700" : "text-gray-800"}`}>
                      {l.ItemDisplay || l.ItemName}
                    </div>
                    {isActivity ? (
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold uppercase text-gray-500 w-14">Actual</label>
                        <QtyInput
                          className="w-36 border-2 rounded-lg px-3 py-1.5 text-right font-medium outline-none focus:border-indigo-400"
                          value={qtyMap[l.JrLineID] ?? 0}
                          onChange={v => setQtyMap(m => ({ ...m, [l.JrLineID]: v }))} />
                        <span className="text-sm text-gray-500">{l.SatuanNama || l.SatuanID}</span>
                        <span className="text-xs text-gray-400 ml-auto">min {formatQty(l.MinUsage)}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Actual: <strong className="text-gray-700">{formatQty(l.ActualQty)}</strong></span>
                        <span>Billed: <strong className="text-gray-700">{formatQty(l.BilledQty)}</strong></span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
